import { useRef } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    TSTFilterBar,
    TSTTable,
    TSTPagination,
    TableToolbar,
    ToolbarSpacer,
    TSTAdjustView,
} from "@impero-com/ui";
import { FilterValueWithMode } from "@impero-com/ui/domain";

import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { DASHBOARD_PENDING_ACTIVITIES_LIST_COLUMN_IDS } from "./consts";
import { PendingActivityTypeFilterRow } from "./PendingActivityTypeFilterRow";
import { PendingTestsMovedAlert } from "./PendingTestsMovedAlert";
import { PendingActionType, PendingActivityTableRow } from "./types";
import { useDashboardPendingActivityListTable } from "./useDashboardPendingActivityListTable";

export function DashboardPendingActivitiesList({
    rows,
    arrivedFromPendingTests,
}: {
    rows: PendingActivityTableRow[];
    arrivedFromPendingTests: boolean;
}) {
    const { t } = useTranslation();
    const tableRef = useRef<HTMLTableElement>(null);

    const controlTestingEnabled = useFeatureFlag("controlTesting");
    const {
        table,
        getFilterComponent,
        canAddException,
        tableId,
        typeFilter,
        clearAllFilters,
    } = useDashboardPendingActivityListTable(rows);

    const typeFilterCount = typeFilter.isNonDefault ? 1 : 0;
    const modeConfig = getFilterModeConfig(t);

    const showOnlyTests = () => {
        const onlyTests: FilterValueWithMode<PendingActionType[]> = {
            value: ["Test", "TestReview"],
            mode: "is",
        };
        table
            .getColumn(DASHBOARD_PENDING_ACTIVITIES_LIST_COLUMN_IDS.ACTION)
            ?.setFilterValue(onlyTests);
    };

    return (
        <div>
            {controlTestingEnabled && arrivedFromPendingTests && (
                <PendingTestsMovedAlert onShowOnlyTests={showOnlyTests} />
            )}
            <TSTFilterBar<PendingActivityTableRow>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                modeConfig={modeConfig}
                belowFilters={
                    controlTestingEnabled ? (
                        <PendingActivityTypeFilterRow typeFilter={typeFilter} />
                    ) : undefined
                }
                extraFilterCount={
                    controlTestingEnabled ? typeFilterCount : undefined
                }
                onClearFilters={clearAllFilters}
            />
            <TSTTable
                tableRef={tableRef}
                table={table}
                noResultsFallback={t("NoResults")}
                noSourceRowsFallback={t("NoResults")}
                toolbar={
                    <TableToolbar>
                        <ToolbarSpacer />
                        <TSTAdjustView
                            table={table}
                            columnsLabel={t("AdjustView_ColumnsHeader")}
                            reorderHintLabel={t("AdjustView_DragToReorderHint")}
                            resetLabel={t("AdjustView_Reset")}
                            pinLabel={t("AdjustView_PinColumn")}
                            unpinLabel={t("AdjustView_UnpinColumn")}
                            containerRef={tableRef}
                        >
                            {t("ControlResult_AdjustView")}
                        </TSTAdjustView>
                    </TableToolbar>
                }
            />
            <TSTPagination
                tableElement={tableRef}
                table={table}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
